import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "../../data/teacher";
import { userRequest } from "../../utils/requestMethods";
import SkeletonCards from "../common/SkeletonCards";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useClassGroups } from "../../hooks/useClassGroups";
import * as api from "../../utils/classGroupApi";
import { splitArmName } from "../../utils/classGroupHelpers";

const AssignedClassesSection = ({ teacher, school }) => {
  const { classGroups, isEnabled: isGroupingEnabled } = useClassGroups();
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [expandedPermissions, setExpandedPermissions] = useState({});
  const [permissionsPopoverId, setPermissionsPopoverId] = useState(null);

  const fetchClasses = async () => {
    setLoadingClasses(true);
    try {
      const { data } = await userRequest.get(
        `/teachers/teacher-assigned-classes/${school?._id}/${teacher._id}?limit=50`,
      );
      setClasses(data?.classes || []);
      setExpandedPermissions({});
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  };

  useEffect(() => {
    if (school?._id && teacher?._id) {
      fetchClasses();
    }
  }, [school?._id, teacher?._id]);

  useEffect(() => {
    const closePopoverOnOutsideClick = (event) => {
      if (!event.target.closest("[data-permissions-popover]")) {
        setPermissionsPopoverId(null);
      }
    };

    if (permissionsPopoverId !== null) {
      document.addEventListener("mousedown", closePopoverOnOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", closePopoverOnOutsideClick);
    };
  }, [permissionsPopoverId]);

  const getRoleBadge = (role) => {
    const roleColors = {
      primary: "bg-blue-100 text-blue-800 border-blue-200",
      assistant: "bg-purple-100 text-purple-800 border-purple-200",
      coordinator: "bg-green-100 text-green-800 border-green-200",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
          roleColors[role] || "bg-gray-100 text-gray-800 border-gray-200"
        }`}
      >
        {role?.charAt(0).toUpperCase() + role?.slice(1) || "Teacher"}
      </span>
    );
  };

  const formatPermission = (permission) => {
    return permission
      ?.split("_")
      ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      ?.join(" ");
  };

  const togglePermissions = (id) => {
    setExpandedPermissions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // One card per class. A class that's an arm of a class level gets a "Part of <level>" tag;
  // arms aren't merged because the teacher's role and permissions can differ per arm.
  const assignments = useMemo(
    () =>
      classes.map((assignment, index) => {
        const classData = assignment.class || assignment;
        const groupId = isGroupingEnabled ? api.getClassGroupId(classData.class_group_id) : null;
        const groupName = groupId
          ? (classGroups || []).find((g) => String(g._id) === String(groupId))?.name ||
            splitArmName(classData.class_name)?.base ||
            null
          : null;
        return {
          classData,
          groupName,
          assignmentId: assignment.assignmentId || assignment._id || index,
          role: assignment.teacher_role || assignment.role,
          permissions: assignment.teacher_permissions || assignment.permissions || [],
        };
      }),
    [classes, classGroups, isGroupingEnabled],
  );

  return (
    <div className="flex flex-wrap items-stretch mt-6 mb-6">
      {loadingClasses ? (
        <SkeletonCards cardCount={3} layout="vertical" width="md:w-1/3 w-full mb-2" />
      ) : classes.length > 0 ? (
        <>
          {assignments.map((item) => {
            const { classData, groupName, assignmentId, role, permissions } = item;

            return (
              <div key={assignmentId} className="pr-3 md:w-1/3 w-full mb-4">
                <div className="border border-[#C5C5E8] bg-white p-3 rounded-2xl flex flex-col justify-between h-full shadow-xs">
                  <div>
                    <div className="bg-[#EEEEFC] flex items-center justify-center rounded-2xl px-4 py-10">
                      <img src={Icons.book} alt="Class Icon" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900">
                          {classData.class_name || "Class Name"}
                        </p>
                        {groupName && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                            Part of {groupName}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        {classData.total_students || classData.studentCount || 0} Students
                      </p>

                      {/* Role Badge */}
                      {role && (
                        <div className="flex items-center gap-2 mt-2 mb-2">
                          {getRoleBadge(role)}
                        </div>
                      )}

                      {/* Permissions */}
                      {permissions && permissions.length > 0 && (
                        <div className="mt-2 mb-3 relative" data-permissions-popover>
                          <button
                            type="button"
                            onClick={() => togglePermissions(assignmentId)}
                            className="flex items-center justify-between w-full text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
                          >
                            <span>Permissions ({permissions.length}):</span>
                            {expandedPermissions[assignmentId] ? (
                              <FiChevronUp className="w-3 h-3" />
                            ) : (
                              <FiChevronDown className="w-3 h-3" />
                            )}
                          </button>
                          <div
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                              expandedPermissions[assignmentId]
                                ? "max-h-20 opacity-100 mt-2"
                                : "max-h-0 opacity-0"
                            }`}
                          >
                            <div className="flex flex-wrap gap-1 relative">
                              {permissions.slice(0, 3).map((permission, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                >
                                  {formatPermission(permission)}
                                </span>
                              ))}
                              {permissions.length > 3 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPermissionsPopoverId(
                                      permissionsPopoverId === assignmentId ? null : assignmentId,
                                    );
                                  }}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200 hover:bg-indigo-200 transition-colors"
                                >
                                  +{permissions.length - 3} more
                                </button>
                              )}
                            </div>
                          </div>
                          {permissionsPopoverId === assignmentId && (
                            <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg p-3 z-20 max-h-64 overflow-y-auto">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-slate-700">
                                  All Permissions ({permissions.length})
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPermissionsPopoverId(null);
                                  }}
                                  className="text-slate-400 hover:text-slate-600 text-xs"
                                >
                                  ×
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {permissions.map((permission, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                  >
                                    {formatPermission(permission)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between mt-3">
                    <Link to={`/t/attendance/mark/${classData._id}`} className="md:w-1/2 w-full">
                      <button className="w-full bg-indigo-200 text-[#4543B5] hover:bg-indigo-100 font-semibold text-xs sm:px-10 px-2 py-2 rounded-full flex items-center justify-center">
                        Mark Attendance
                      </button>
                    </Link>
                    <Link
                      to={`/t/results/students/${classData._id}`}
                      className="w-full sm:pr-3 pr-0 md:w-1/2 w-full sm:mt-0 mt-2 sm:pl-2 pl-0 flex items-center justify-end"
                    >
                      <button className="w-full bg-[#4543B5] hover:bg-indigo-900 text-white font-semibold text-xs sm:px-10 px-2 py-2 rounded-full flex items-center justify-center">
                        Upload Result
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className="text-center w-full text-gray-500 py-12">
          Teacher is not assigned to any class.
        </div>
      )}
    </div>
  );
};

export default AssignedClassesSection;
